import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  StaysFormData,
  StaysWidgetForm
} from '@com-delta-idp/mnx-sitewide-header-ui';

@Injectable({
  providedIn: 'root'
})
export class BookingStaysFormService {
  private travelerRoomValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const passengers = control.get('numberOfTravelers')?.value;
    const rooms = control.get('numberOfRooms')?.value;
    if (!passengers || !rooms) return null;
    if (passengers < rooms) {
      return { staysRoomMismatch: { min: rooms, current: passengers } };
    }
    return null;
  }

  private roomValidator(control: AbstractControl): ValidationErrors | null {
    const passengers = control.get('numberOfTravelers')?.value;
    const rooms = control.get('numberOfRooms')?.value;
    if (!passengers || !rooms) return null;
    if (passengers > rooms * 4) {
      return {
        staysRoomTravelerMismatch: { min: rooms, current: passengers }
      };
    }
    return null;
  }

  private dateValidator(control: AbstractControl): ValidationErrors | null {
    const checkInDate = control.get('checkInDate')?.value;
    const checkOutDate = control.get('checkOutDate')?.value;
    if (!checkInDate || !checkOutDate) return null;
    const diffInMs = checkOutDate.getTime() - checkInDate.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays > 28) {
      return {
        maxDateMismatch: { min: checkOutDate, current: checkInDate }
      };
    }
    return null;
  }

  createBookingForm(): FormGroup<StaysWidgetForm> {
    return new FormGroup(
      {
        location: new FormControl('', {
          validators: [Validators.required],
          nonNullable: true
        }),
        checkInDate: new FormControl<Date | null>(null, {
          validators: [Validators.required]
        }),
        checkOutDate: new FormControl<Date | null>(null, {
          validators: [Validators.required]
        }),
        numberOfTravelers: new FormControl(1, {
          validators: [Validators.required],
          nonNullable: true
        }),
        numberOfRooms: new FormControl(1, {
          validators: [Validators.required],
          nonNullable: true
        })
      },
      {
        validators: [
          this.travelerRoomValidator,
          this.roomValidator,
          this.dateValidator
        ]
      }
    );
  }

  distributedPassengersToRooms(
    numberOfTravelers: number,
    numberOfRooms: number
  ): string {
    const adultsPerRoom = Math.floor(numberOfTravelers / numberOfRooms);
    let remainder = numberOfTravelers % numberOfRooms;

    const roomOccupancy: number[] = [];

    for (let i = 0; i < numberOfRooms; i++) {
      const occupancy = remainder > 0 ? adultsPerRoom + 1 : adultsPerRoom;
      roomOccupancy.push(occupancy);
      if (remainder > 0) {
        remainder--;
      }
    }

    return roomOccupancy.join(',');
  }

  navigateToStaysSearch(
    formValues: StaysFormData,
    adultsPerRoom: string,
    mdpcid: string
  ): void {
    const baseUrl = `${
      window.location.protocol
    }//carsandstays.${'delta.com'.replace(/^www\./, '')}/Hotel-Search`;

    const params = new URLSearchParams({
      MDPCID: mdpcid,
      destination: formValues.location,
      startDate: formValues.checkInDate?.toISOString().split('T')[0] || '',
      endDate: formValues.checkOutDate?.toISOString().split('T')[0] || '',
      adults: adultsPerRoom
    });

    const url = `${baseUrl}?${params.toString()}`;

    window.open(url, '_blank');
  }
}
